import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Divider,
  Alert,
  LinearProgress,
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { validatePassword, getPasswordStrength } from '../../utils/passwordValidation';
import './Signup.css';

function Signup() {
  const navigate = useNavigate();
  const { signup, googleSignIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [passwordStrength, setPasswordStrength] = useState(0);

  useEffect(() => {
    if (password) {
      const { errors } = validatePassword(password);
      setPasswordErrors(errors);
      setPasswordStrength(getPasswordStrength(password));
    } else {
      setPasswordErrors([]);
      setPasswordStrength(0);
    }
  }, [password]);

  const getStrengthColor = (strength) => {
    if (strength < 30) return '#f44336';
    if (strength < 50) return '#ff9800';
    if (strength < 80) return '#ffd700';
    return '#4caf50';
  };

  async function handleSubmit(e) {
    e.preventDefault();

    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }

    const { isValid, errors } = validatePassword(password);
    if (!isValid) {
      return setError('Password does not meet requirements: ' + errors.join(', '));
    }

    try {
      setError('');
      setLoading(true);
      await signup(email, password);
      navigate('/onboarding');
    } catch (error) {
      setError('Failed to create an account: ' + error.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSignIn() {
    try {
      setError('');
      setLoading(true);
      await googleSignIn();
      navigate('/onboarding');
    } catch (error) {
      setError('Failed to sign in with Google: ' + error.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Container maxWidth={false} className="signup-container">
      <Paper elevation={0} className="signup-card">
        <Typography variant="h5" className="signup-title">
          Create Account
        </Typography>

        {error && (
          <Alert severity="error" className="signup-alert">
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            variant="outlined"
            required
            autoFocus
            autoComplete="email"
          />
          <Box sx={{ width: '100%' }}>
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="outlined"
              required
              autoComplete="new-password"
              fullWidth
            />
            {password && (
              <>
                <Box sx={{ mt: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={passwordStrength}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getStrengthColor(passwordStrength),
                      },
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: getStrengthColor(passwordStrength),
                    mt: 0.5,
                    display: 'block',
                  }}
                >
                  Password Strength: {passwordStrength}%
                </Typography>
                {passwordErrors.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    {passwordErrors.map((error, index) => (
                      <Typography
                        key={index}
                        variant="caption"
                        color="error"
                        display="block"
                      >
                        • {error}
                      </Typography>
                    ))}
                  </Box>
                )}
              </>
            )}
          </Box>
          <TextField
            label="Confirm Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            variant="outlined"
            required
            autoComplete="new-password"
          />
          <Button
            type="submit"
            variant="contained"
            disabled={loading || passwordErrors.length > 0}
            className="signup-button"
          >
            Sign Up
          </Button>
        </form>

        <Box className="signup-divider">
          <Divider>or continue with</Divider>
        </Box>

        <Button
          fullWidth
          variant="outlined"
          startIcon={<GoogleIcon />}
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="google-button"
        >
          Google
        </Button>

        <Box className="signup-footer">
          <Typography variant="body2" color="textSecondary">
            Already have an account?{' '}
            <Link to="/login" className="signup-link">
              Sign In
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
}

export default Signup;
